// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { IButtonStyles } from '@fluentui/react';
import { CSSProperties } from 'react';

// Color constants
const colors = {
  primary: '#0078D4',
  white: '#FFFFFF',
  darkGray: '#333333',
  mediumGray: '#555',
  lightGray: '#666666',
  borderGray: '#E0E0E0',
  disabledGray: '#cccccc',
  iconGray: '#777',
  backgroundLight: '#F0F0F0',
  buttonBorder: '#DADADA',
  textDark: 'rgb(59, 58, 57)'
} as const;

// export const pulseAnimation = `
//   @keyframes pulse {
//     0% {
//       transform: scale(0.95);
//       box-shadow: 0 0 0 0 rgba(0, 120, 212, 0.7);
//     }
//     70% {
//       transform: scale(1);
//       box-shadow: 0 0 0 0.625rem rgba(0, 120, 212, 0);
//     }
//     100% {
//       transform: scale(0.95);
//       box-shadow: 0 0 0 0 rgba(0, 120, 212, 0);
//     }
//   }
// `;

export const baseWidgetStyle: CSSProperties = {
  fontFamily: 'Arial, sans-serif',
  borderRadius: '0.75rem', // 12px
  boxShadow: '0 0.25rem 0.75rem rgba(0, 0, 0, 0.1)', // 0 4px 12px
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  // position: 'fixed',
  zIndex: 1000,
  justifyContent: 'center',
  backgroundColor: colors.white,
  // width: '18.75rem', // 300px
  width: '100%',
  height: 'auto',
  padding: '1.5rem', // 24px
  paddingBottom: '1.25rem', // 20px
  bottom: '1.25rem', // 20px
  right: '1.25rem' // 20px
};

export const expertCardStyle: CSSProperties = {
  ...baseWidgetStyle
};

export const agentSpeakingViewBaseStyle: CSSProperties = {
  ...baseWidgetStyle
};

export const agentSpeakingCardStyle: CSSProperties = {
  ...agentSpeakingViewBaseStyle
};

export const fullscreenViewStyle: CSSProperties = {
  ...agentSpeakingViewBaseStyle,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100%',
  height: '100%',
  borderRadius: '0',
  padding: '1.25rem', // 20px
  justifyContent: 'space-between',
  zIndex: 2000
};

export const pulsatingLogoWrapperBaseStyle: CSSProperties = {
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: colors.white
};

// export const cardPulsatingLogoWrapperStyle: CSSProperties = {
//   ...pulsatingLogoWrapperBaseStyle,
//   width: '5rem', // 80px
//   height: '5rem', // 80px
//   marginBottom: '0.625rem', // 10px
//   borderRadius: '50%',
//   background: colors.white
// };

export const fullscreenPulsatingLogoWrapperStyle: CSSProperties = {
  ...pulsatingLogoWrapperBaseStyle,
  width: '7.5rem', // 120px
  height: '7.5rem' // 120px
};

export const titleStyle: CSSProperties = {
  fontSize: '1.125rem', // 18px
  fontWeight: 'bold',
  color: colors.darkGray,
  margin: '0 0 0.5rem 0' // 0 0 8px 0
};

export const subtitleStyle: CSSProperties = {
  fontSize: '0.875rem', // 14px
  color: colors.lightGray,
  margin: '0 0 1.25rem 0', // 0 0 20px 0
  lineHeight: '1.4'
};

export const getAdviceButtonStyle: CSSProperties = {
  padding: '10px 20px',
  marginTop: 0,
  fontSize: 16,
  fontWeight: 600,
  backgroundColor: '#00d2aa',
  color: colors.white,
  display: 'flex',
  alignItems: 'center',
  borderRadius: '6px',
  cursor: 'pointer'
};

export const collapsedButtonStyle: CSSProperties = {
  padding: '10px 20px',
  marginTop: 40,
  fontSize: 16,
  fontWeight: 600,
  backgroundColor: '#004140',
  color: colors.white,
  display: 'flex',
  alignItems: 'center',
  borderRadius: '6px',
  cursor: 'pointer'
};

export const agentSpeakingTextBaseStyle: CSSProperties = {
  color: '#333',
  margin: '0.125rem 0' // 2px 0
};

export const cardAgentSpeakingTextStyleContoso: CSSProperties = {
  ...agentSpeakingTextBaseStyle,
  fontSize: '0.8125rem', // 13px
  color: colors.mediumGray
};

export const cardAgentSpeakingTextStyleStatus: CSSProperties = {
  ...agentSpeakingTextBaseStyle,
  fontSize: '1.125rem', // 18px
  fontWeight: 'bold'
};

export const fullscreenAgentSpeakingTextStyleContoso: CSSProperties = {
  ...agentSpeakingTextBaseStyle,
  fontSize: '1rem', // 16px
  color: colors.mediumGray,
  marginTop: '0.9375rem' // 15px
};

export const fullscreenAgentSpeakingTextStyleStatus: CSSProperties = {
  ...agentSpeakingTextBaseStyle,
  fontSize: '1.5rem', // 24px
  fontWeight: 'bold'
};

export const iconButtonContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'row',
  width: '100%',
  marginTop: '1.5625rem' // 25px
};

export const fullscreenIconButtonContainerStyle: CSSProperties = {
  ...iconButtonContainerStyle,
  marginTop: 'auto',
  paddingBottom: '2.5rem' // 40px
};

export const fullscreenButtonStyle: CSSProperties = {
  position: 'absolute',
  top: '0.625rem', // 10px - changed from 0.9375rem (15px) to match closeButtonStyle
  right: '0.625rem', // 10px - changed from 0.9375rem (15px) to match closeButtonStyle
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: colors.mediumGray,
  padding: '0.3125rem' // 5px
};

export const closeButtonStyle: React.CSSProperties = {
  position: 'absolute',
  top: '0.625rem', // 10px
  right: '0.625rem', // 10px
  background: 'none',
  border: 'none',
  fontSize: '1.5rem', // 24px
  cursor: 'pointer',
  color: colors.iconGray,
  padding: '0',
  lineHeight: '1'
};

export const getAdviceButtonDisabledStyle: React.CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  backgroundColor: '#00d2aa',
  color: colors.lightGray
};

export const getAdviceButtonLoadingStyle: React.CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed'
};

export const iconButtonStyles: IButtonStyles = {
  root: {
    background: '#00d2aa',
    border: `1px solid #00d2aa`,
    borderRadius: '50%',
    width: '2.75rem', // 44px
    margin: '0 0.625rem', // 0 10px
    color: colors.white,
    i: {
      color: colors.white
    }
  },
  rootHovered: {
    background: '#00b894',
    borderColor: '#00b894',
    color: colors.white,
    i: {
      color: colors.white
    }
  },
  rootPressed: {
    background: '#009f87',
    borderColor: '#009f87',
    color: colors.white,
    i: {
      color: colors.white
    }
  },
  rootDisabled: {
    background: '#00d2aa',
    borderColor: '#00d2aa',
    color: colors.white,
    opacity: 0.4,
    i: {
      color: colors.white
    }
  },
  // Mic button is a toggle (checked = unmuted); use same green hover as root so no gray
  rootChecked: {
    background: '#00d2aa',
    borderColor: '#00d2aa',
    color: colors.white,
    i: {
      color: colors.white
    }
  },
  rootCheckedHovered: {
    background: '#00b894',
    borderColor: '#00b894',
    color: colors.white,
    i: {
      color: colors.white
    }
  },
  rootCheckedPressed: {
    background: '#009f87',
    borderColor: '#009f87',
    color: colors.white,
    i: {
      color: colors.white
    }
  }
};

/** Red styles for end call button */
export const endCallButtonStyles: IButtonStyles = {
  root: {
    background: '#ff3d52',
    border: '1px solid #ff3d52',
    borderRadius: '50%',
    width: '2.75rem',
    margin: '0 0.625rem',
    color: colors.white,
    i: {
      color: colors.white
    }
  },
  rootHovered: {
    background: '#e63548',
    borderColor: '#e63548',
    color: colors.white,
    i: {
      color: colors.white
    }
  },
  rootPressed: {
    background: '#cc2d3e',
    borderColor: '#cc2d3e',
    color: colors.white,
    i: {
      color: colors.white
    }
  }
};

export const waveContainerStyle: CSSProperties = {
  position: 'relative',
  width: 750,
  height: 212,
  overflow: 'hidden',
  pointerEvents: 'none'
};

export const messageWrapperStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  maxWidth: '90%',
  margin: '12px auto 0 auto'
};

/** Message text: preserves newlines so question and bullet points display on separate lines */
export const messageTextStyle: CSSProperties = {
  whiteSpace: 'pre-line',
  lineHeight: 1.5,
  margin: 0,
  fontSize: '18px',
  color: '#004140',
  textAlign: 'left'
};

/** When question has bullet points: width auto, centered with margin 0 auto */
export const messageTextWithBulletsStyle: CSSProperties = {
  ...messageTextStyle,
  display: 'inline-block',
  width: '700px',
  margin: '0 auto'
};

export const navIconButtonLeftStyle: CSSProperties = {
  position: 'absolute',
  left: -40,
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  color: '#333'
};

export const navIconButtonRightStyle: CSSProperties = {
  position: 'absolute',
  right: -40,
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 10,
  color: '#333'
};

export const messageScrollContainerStyle: CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  maxHeight: 250,
  overflowY: 'auto',
  padding: 12,
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 8
};

export const globalAnimations = `
@keyframes slideInRight {
  from { opacity: 0; transform: translateX(40px); }
  to   { opacity: 1; transform: translateX(0); }
}

`;
export const slideInRightStyle: CSSProperties = {
  animation: 'slideInRight 4s ease forwards'
};

// interview stepper //

export const interviewStepperContainerStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column'
};

export const stepperWrapperStyle: CSSProperties = {
  maxWidth: 1000,
  width: '100%',
  padding: '20px 0',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: 'auto'
};

export const stepIconStyle = (active?: boolean, completed?: boolean): CSSProperties => ({
  width: active ? '23px' : '20px',
  height: active ? '23px' : '20px',
  borderRadius: '50%',
  background: completed ? '#00D2AA' : '#fff',
  border: completed ? 'none' : active ? '3px solid #00D2AA' : '1px solid #2A312B66',
  margin: '0 auto',
  transition: 'all 0.3s ease',
  zIndex: 9
});

export const stepperContainerStyle: CSSProperties = {
  marginTop: 20,
  width: '100%'
};

// Show Transcript button (bottom left during call)
export const showTranscriptButtonStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  padding: '10px 20px',
  backgroundColor: colors.white,
  border: '1px solid #E7E5E4',
  borderRadius: 24,
  boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.04em',
  color: '#44403C',
  cursor: 'pointer',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
};

// Live Transcript panel (fixed right, 400px)
export const transcriptPanelStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  width: 400,
  backgroundColor: colors.white,
  boxShadow: '-2px 0 12px rgba(0,0,0,0.08)',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 3000
};

export const transcriptPanelHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '20px 24px',
  borderBottom: '1px solid #E7E5E4',
  flexShrink: 0
};

export const transcriptPanelTitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 700,
  color: '#1C1917'
};

export const transcriptPanelCloseStyle: CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  border: 'none',
  background: 'transparent',
  color: '#44403C',
  fontSize: 18,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0
};

export const transcriptPanelContentStyle: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: 24
};

export const transcriptBubbleStyle: CSSProperties = {
  backgroundColor: '#F8F8F8',
  borderRadius: 12,
  padding: '14px 16px',
  marginBottom: 12
};

export const transcriptBubbleLabelStyle: CSSProperties = {
  margin: '0 0 6px 0',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.04em',
  color: '#0D9488',
  textTransform: 'uppercase'
};

export const transcriptBubbleTextStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  color: '#1C1917',
  lineHeight: 1.5
};

export const transcriptListeningStyle: CSSProperties = {
  backgroundColor: '#F0F0F0',
  borderRadius: 12,
  padding: '14px 16px',
  marginTop: 8,
  fontStyle: 'italic',
  fontSize: 14,
  color: '#78716C'
};

export const transcriptBottomBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 8
};

export const callScreenBottomBarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  marginTop: '1.5625rem',
  paddingTop: 8,
  gap: 16
};

export const callScreenBottomBarCenterStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  minWidth: 0
};

export const callScreenBottomBarRightStyle: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end'
};

// Assessment progress stepper (compact, bottom right) - matches design: 5 segments, active blue, inactive grey
export const assessmentProgressRootStyle: CSSProperties = {
  maxWidth: 240,
  width: '100%'
};

export const assessmentProgressLabelStyle: CSSProperties = {
  margin: '0 0 8px 0',
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.08em',
  color: '#9CA3AF',
  textTransform: 'uppercase' as const
};

export const assessmentProgressSegmentsStyle: CSSProperties = {
  display: 'flex',
  gap: 4,
  width: '100%'
};

export const assessmentProgressSegmentBaseStyle: CSSProperties = {
  flex: 1,
  height: 8,
  borderRadius: 4,
  minWidth: 0,
  transition: 'background-color 0.2s ease'
};

export const assessmentProgressSegmentActiveStyle: CSSProperties = {
  ...assessmentProgressSegmentBaseStyle,
  backgroundColor: '#3F51B5'
};

export const assessmentProgressSegmentInactiveStyle: CSSProperties = {
  ...assessmentProgressSegmentBaseStyle,
  backgroundColor: '#E0E0E0'
};
